import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Search, UserPlus, ShieldAlert, ShieldCheck, Mail, KeyRound, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { nameError, emailError, mobileError, textError, selectError, apiFieldError } from '@/lib/formValidation';
import { FIELD_CLASS, EMAIL_PROPS, TEL_PROPS } from '@/lib/mobileForm';

const FieldHint = ({ error }) => (
  error ? <p className="text-[11px] text-red-600 mt-1" data-testid="field-error">{error}</p> : null
);

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Platform Admin' },
  { value: 'safeguarding', label: 'Safeguarding permission' },
  { value: 'coordinator', label: 'Career Coordinator' },
  { value: 'parent', label: 'Parent' },
  { value: 'partner', label: 'Industry Partner' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'pending_activation', label: 'Pending activation' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'school_suspended', label: 'School suspended' },
];

const STATUS_STYLE = {
  active: 'bg-green-50 text-green-600',
  pending_activation: 'bg-amber-50 text-amber-600',
  disabled: 'bg-red-50 text-red-600',
  school_suspended: 'bg-orange-50 text-orange-600',
};

const ROLE_STYLE = {
  'Platform Admin': 'bg-[#FFF0ED] text-[#F97D6A]',
  Safeguarding: 'bg-emerald-50 text-emerald-700',
  'Career Coordinator': 'bg-blue-50 text-blue-600',
  Parent: 'bg-slate-100 text-slate-600',
  'Industry Partner': 'bg-indigo-50 text-indigo-600',
  Mentor: 'bg-purple-50 text-purple-600',
};

const fmt = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtTime = (value) => {
  if (!value) return 'Never';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'Never';
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const RoleBadges = ({ roles }) => (
  <div className="flex flex-wrap gap-1">
    {(roles || []).map((r) => (
      <Badge key={r} className={`border-0 text-[10px] ${ROLE_STYLE[r] || 'bg-gray-100 text-gray-500'}`}>{r}</Badge>
    ))}
  </div>
);

// ─── Create account dialog ───
function CreateUserDialog({ schools, onClose, onDone }) {
  const [form, setForm] = useState({
    role: 'coordinator', full_name: '', email: '', mobile: '', designation: '',
    school_id: '', coordinator_type: 'primary',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const needsSchool = form.role !== 'admin';

  const validate = () => {
    const next = {
      full_name: nameError(form.full_name, 'Full name'),
      email: emailError(form.email, 'Email'),
      mobile: mobileError(form.mobile, 'Mobile', { required: form.role === 'coordinator' }),
      designation: textError(form.designation, 'Designation', { required: false }),
      school_id: needsSchool ? selectError(form.school_id, 'School') : '',
    };
    setErrors(next);
    return Object.values(next).every((v) => !v);
  };

  const setField = (key, value) => {
    setForm({ ...form, [key]: value });
    if (errors[key]) setErrors({ ...errors, [key]: '' });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validate()) return;
    setBusy(true);
    try {
      if (form.role === 'coordinator') {
        // Reuses the existing coordinator creation + activation workflow
        await api.post('/coordinators', {
          full_name: form.full_name, email: form.email, mobile: form.mobile,
          designation: form.designation, school_id: form.school_id,
          coordinator_type: form.coordinator_type,
        });
      } else {
        await api.post('/admin/users', {
          role: form.role, full_name: form.full_name, email: form.email,
          mobile: form.mobile, designation: form.designation,
          school_id: form.role === 'admin' ? null : form.school_id,
        });
      }
      toast.success('Account created with Pending activation. Send the activation link next.');
      onDone();
    } catch (err) {
      const { field, message } = apiFieldError(err, 'Could not create this account');
      if (field) setErrors((prev) => ({ ...prev, [field]: message }));
      else setError(message);
    }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl" data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">Add a user</DialogTitle>
          <DialogDescription>
            Platform Admin can create Platform Admin and Career Coordinator accounts only.
          </DialogDescription>
        </DialogHeader>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3" data-testid="create-user-error">{error}</div>}
        <form onSubmit={submit} noValidate className="space-y-4">
          <div>
            <Label>Account type</Label>
            <Select value={form.role} onValueChange={(v) => { setForm({ ...form, role: v }); setErrors({}); }}>
              <SelectTrigger className="mt-1 rounded-xl h-11 sm:h-10 text-base sm:text-sm border-gray-200" data-testid="new-user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Platform Admin</SelectItem>
                <SelectItem value="coordinator">Career Coordinator</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Full name</Label>
            <Input value={form.full_name} onChange={(e) => setField('full_name', e.target.value)} className={`mt-1 rounded-xl ${FIELD_CLASS} ${errors.full_name ? 'border-red-400' : 'border-gray-200'}`} data-testid="new-user-name-input" />
            <FieldHint error={errors.full_name} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Email</Label>
              <Input {...EMAIL_PROPS} value={form.email} onChange={(e) => setField('email', e.target.value)} className={`mt-1 rounded-xl ${FIELD_CLASS} ${errors.email ? 'border-red-400' : 'border-gray-200'}`} data-testid="new-user-email-input" />
              <FieldHint error={errors.email} />
            </div>
            <div>
              <Label>Mobile {form.role === 'coordinator' ? '' : '(optional)'}</Label>
              <Input {...TEL_PROPS} value={form.mobile} onChange={(e) => setField('mobile', e.target.value)} placeholder="+919876543210" className={`mt-1 rounded-xl ${FIELD_CLASS} ${errors.mobile ? 'border-red-400' : 'border-gray-200'}`} data-testid="new-user-mobile-input" />
              <FieldHint error={errors.mobile} />
            </div>
          </div>
          <div>
            <Label>Designation (optional)</Label>
            <Input value={form.designation} onChange={(e) => setField('designation', e.target.value)} placeholder="e.g. Designated Safeguarding Lead" className={`mt-1 rounded-xl ${FIELD_CLASS} ${errors.designation ? 'border-red-400' : 'border-gray-200'}`} data-testid="new-user-designation-input" />
            <FieldHint error={errors.designation} />
          </div>
          {needsSchool && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>School</Label>
                <Select value={form.school_id} onValueChange={(v) => setField('school_id', v)}>
                  <SelectTrigger className={`mt-1 rounded-xl h-11 sm:h-10 text-base sm:text-sm ${errors.school_id ? 'border-red-400' : 'border-gray-200'}`} data-testid="new-user-school-select"><SelectValue placeholder="Select school" /></SelectTrigger>
                  <SelectContent>
                    {schools.map((s) => <SelectItem key={s.school_id} value={s.school_id}>{s.school_name} ({s.school_id})</SelectItem>)}
                  </SelectContent>
                </Select>
                <FieldHint error={errors.school_id} />
              </div>
              {form.role === 'coordinator' && (
                <div>
                  <Label>Coordinator type</Label>
                  <Select value={form.coordinator_type} onValueChange={(v) => setForm({ ...form, coordinator_type: v })}>
                    <SelectTrigger className="mt-1 rounded-xl h-11 sm:h-10 text-base sm:text-sm border-gray-200" data-testid="new-user-coord-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="primary">Primary</SelectItem>
                      <SelectItem value="safeguarding">Safeguarding</SelectItem>
                      <SelectItem value="backup">Backup</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          <div className="bg-blue-50 rounded-xl px-4 py-3 text-xs text-blue-700">
            Parents, mentors, students and industry partners are created and managed by the Career
            Coordinator — they cannot be added here.
          </div>
          {form.role === 'coordinator' && form.coordinator_type === 'safeguarding' && (
            <div className="bg-emerald-50 rounded-xl px-4 py-3 text-xs text-emerald-700" data-testid="safeguarding-role-note">
              This coordinator will receive the <strong>Safeguarding permission</strong> for their
              school: full Career Coordinator access plus the school's Student Safety section.
              The permission can be revoked any time from the Coordinators tab.
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-11 sm:h-10 w-full sm:w-auto" data-testid="create-user-submit">
              {busy ? 'Creating…' : 'Create account'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Disable / enable confirmation ───
function AccessChangeDialog({ account, mode, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const disabling = mode === 'disable';

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await api.post(`/admin/users/${account.id}/${mode}`, { reason });
      toast.success(res.data.message);
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not update access');
    }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="access-change-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] flex items-center gap-2">
            {disabling ? <ShieldAlert className="h-5 w-5 text-red-500" /> : <ShieldCheck className="h-5 w-5 text-green-600" />}
            {disabling ? 'Disable sign-in' : 'Restore sign-in'}
          </DialogTitle>
          <DialogDescription>
            {disabling
              ? `${account.full_name} will no longer be able to sign in. Every student, consent, internship, mentoring and safeguarding record stays exactly as it is.`
              : `${account.full_name} will be able to sign in again.`}
          </DialogDescription>
        </DialogHeader>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3" data-testid="access-change-error">{error}</div>}
        <div>
          <Label>Reason (required)</Label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                    placeholder={disabling ? 'e.g. Staff member has left the school' : 'e.g. Staff member has returned'}
                    className="mt-1 rounded-xl border-gray-200 text-base sm:text-sm" data-testid="access-reason-input" />
          <p className="text-[11px] text-gray-400 mt-1">Recorded in this account's audit history.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 5}
                  className={`rounded-xl text-white ${disabling ? 'bg-red-500 hover:bg-red-600' : 'bg-green-600 hover:bg-green-700'}`}
                  data-testid="access-change-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : disabling ? 'Disable access' : 'Restore access'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Account detail ───
function UserDetailDialog({ userId, onClose, onChanged }) {
  const [account, setAccount] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [accessMode, setAccessMode] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/admin/users/${userId}`);
      setAccount(res.data);
    } catch (err) {
      setLoadError(err.response?.data?.detail || 'Could not load this account');
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const resend = async () => {
    setSending(true);
    try {
      const res = await api.post('/admin/send-activation', { user_id: userId });
      toast.success(res.data.message);
      await load();
      onChanged();
    } catch (err) {
      toast.error(apiFieldError(err, 'Could not send the invitation').message);
    }
    setSending(false);
  };

  const sendReset = async () => {
    setSending(true);
    try {
      const res = await api.post('/admin/reset-user-password', { user_id: userId });
      toast.success(res.data.message);
      await load();
      onChanged();
    } catch (err) {
      toast.error(apiFieldError(err, 'Could not send the reset link').message);
    }
    setSending(false);
  };

  return (
    <>
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="rounded-2xl sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="user-detail-dialog">
          {!account ? (
            <div className="py-12 text-center" data-testid="user-detail-loading">
              {loadError
                ? <p className="text-sm text-red-600" data-testid="user-detail-error">{loadError}</p>
                : <Loader2 className="h-5 w-5 animate-spin mx-auto text-gray-400" />}
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-['Outfit']" data-testid="user-detail-name">{account.full_name}</DialogTitle>
                <DialogDescription className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-gray-500">{account.email}</span>
                  <Badge className={`border-0 text-[10px] ${STATUS_STYLE[account.account_status] || 'bg-gray-100 text-gray-500'}`} data-testid="user-detail-status">
                    {account.account_status_label}
                  </Badge>
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-3 text-sm" data-testid="user-detail-fields">
                {[
                  ['Roles', null],
                  ['School', account.school_name ? `${account.school_name} (${account.school_id})` : 'Platform level'],
                  ['Mobile', account.mobile || '—'],
                  ['Designation', account.designation || '—'],
                  ['Last login', fmtTime(account.last_login_at)],
                  ['Created', fmt(account.created_at)],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-xl bg-gray-50 px-3 py-2.5">
                    <p className="text-[11px] text-gray-500 mb-1">{label}</p>
                    {value === null ? <RoleBadges roles={account.roles} /> : <p className="font-medium text-[#1F2937] break-words">{value}</p>}
                  </div>
                ))}
              </div>

              {account.account_status === 'disabled' && (
                <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700" data-testid="user-disabled-note">
                  Sign-in disabled on {fmt(account.disabled_at)} — {account.disabled_reason}
                </div>
              )}

              {account.account_status === 'active' && account.role === 'admin' && (
                <p className="text-[11px] text-gray-400" data-testid="admin-reset-note">
                  Platform Admin passwords are reset by the account holder through Forgot Password.
                </p>
              )}

              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF] mb-2">Invitation history</p>
                <div className="rounded-xl border border-gray-200 divide-y divide-gray-50" data-testid="invitation-history">
                  {(account.invitations || []).length === 0 ? (
                    <p className="px-4 py-3 text-sm text-gray-400">No invitation has been sent to this account.</p>
                  ) : account.invitations.map((inv, i) => (
                    <div key={i} className="px-4 py-2.5 flex items-center justify-between text-sm">
                      <div>
                        <p className="text-[#1F2937]">{inv.type}</p>
                        <p className="text-[11px] text-gray-400">Sent {fmtTime(inv.sent_at)}</p>
                      </div>
                      <Badge className={`border-0 text-[10px] ${inv.used ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>
                        {inv.used ? 'Used' : 'Not used'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF] mb-2">Account audit history</p>
                <div className="rounded-xl border border-gray-200 divide-y divide-gray-50" data-testid="account-audit-history">
                  {(account.audit || []).length === 0 ? (
                    <p className="px-4 py-3 text-sm text-gray-400">No account actions recorded yet.</p>
                  ) : account.audit.map((a, i) => (
                    <div key={i} className="px-4 py-2.5 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-medium text-[#1F2937] capitalize">{(a.action || '').replace(/_/g, ' ')}</p>
                        <p className="text-[11px] text-gray-400 whitespace-nowrap">{fmtTime(a.at)}</p>
                      </div>
                      {a.reason && <p className="text-xs text-gray-600 mt-0.5">Reason: {a.reason}</p>}
                      {a.detail && <p className="text-xs text-gray-500 mt-0.5">{a.detail}</p>}
                      <p className="text-[11px] text-gray-400 mt-0.5">by {a.actor_name || 'Platform Admin'}</p>
                    </div>
                  ))}
                </div>
              </div>

              <DialogFooter className="flex-wrap gap-2">
                {account.can_resend_invitation && (
                  <Button variant="outline" onClick={resend} disabled={sending} className="rounded-xl text-sm" data-testid="resend-invitation-btn">
                    {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />}
                    {account.activation_sent_at ? 'Resend invitation' : 'Send invitation'}
                  </Button>
                )}
                {account.can_send_password_reset && (
                  <Button variant="outline" onClick={sendReset} disabled={sending} className="rounded-xl text-sm" data-testid="send-password-reset-btn">
                    {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <KeyRound className="h-4 w-4 mr-1" />}
                    Send password reset link
                  </Button>
                )}
                {account.can_disable && (
                  <Button variant="outline" onClick={() => setAccessMode('disable')} className="rounded-xl text-sm text-red-600 border-red-200 hover:bg-red-50" data-testid="disable-access-btn">
                    <ShieldAlert className="h-4 w-4 mr-1" /> Disable sign-in
                  </Button>
                )}
                {account.can_enable && (
                  <Button variant="outline" onClick={() => setAccessMode('enable')} className="rounded-xl text-sm text-green-700 border-green-200 hover:bg-green-50" data-testid="enable-access-btn">
                    <ShieldCheck className="h-4 w-4 mr-1" /> Restore sign-in
                  </Button>
                )}
                <Button onClick={onClose} className="rounded-xl bg-[#1F2937] text-white hover:bg-black text-sm" data-testid="close-user-detail">Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {accessMode && account && (
        <AccessChangeDialog
          account={account}
          mode={accessMode}
          onClose={() => setAccessMode(null)}
          onDone={async () => { setAccessMode(null); await load(); onChanged(); }}
        />
      )}
    </>
  );
}

// ─── Users tab ───
export default function UsersTab({ schools, schoolFilter, onSchoolFilterChange }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const school = schoolFilter || 'all';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (school !== 'all') params.school_id = school;
      if (roleFilter !== 'all') params.role = roleFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await api.get('/admin/users', { params });
      setUsers(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load user accounts');
    }
    setLoading(false);
  }, [search, school, roleFilter, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const schoolName = schools.find((s) => s.school_id === school)?.school_name;

  return (
    <div data-testid="users-tab">
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email…" className="pl-9 rounded-xl h-11 sm:h-10 text-base sm:text-sm border-gray-200" data-testid="user-search-input" />
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl text-sm font-semibold" data-testid="add-user-btn">
          <UserPlus className="h-4 w-4 mr-1" /> Add user
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Select value={school} onValueChange={(v) => onSchoolFilterChange(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[230px] rounded-xl h-9 border-gray-200 text-sm" data-testid="user-school-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All schools</SelectItem>
            {schools.map((s) => <SelectItem key={s.school_id} value={s.school_id}>{s.school_name} ({s.school_id})</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[180px] rounded-xl h-9 border-gray-200 text-sm" data-testid="user-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] rounded-xl h-9 border-gray-200 text-sm" data-testid="user-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {school !== 'all' && (
          <button onClick={() => onSchoolFilterChange('')} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#F97D6A] hover:underline" data-testid="clear-school-filter">
            <X className="h-3 w-3" /> Showing {schoolName || school} only
          </button>
        )}
        <span className="text-xs text-gray-400 ml-auto" data-testid="users-count">{users.length} {users.length === 1 ? 'account' : 'accounts'}</span>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200">
        <div className="overflow-x-auto rounded-2xl">
          <table className="w-full min-w-[1120px] text-sm" data-testid="users-table">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                {['Name', 'Email', 'Role(s)', 'School', 'Account status', 'Last login', 'Created'].map((h) => (
                  <th key={h} className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#9CA3AF] whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} onClick={() => setDetailId(u.id)}
                    className="border-b border-gray-50 hover:bg-[#FFF8F6] cursor-pointer transition-colors"
                    data-testid={`user-row-${u.id}`}>
                  <td className="py-3 px-4 font-medium text-[#1F2937] whitespace-nowrap">{u.full_name || '—'}</td>
                  <td className="py-3 px-4 text-gray-500">{u.email}</td>
                  <td className="py-3 px-4"><RoleBadges roles={u.roles} /></td>
                  <td className="py-3 px-4 whitespace-nowrap">
                    {u.school_id
                      ? <Badge className="bg-blue-50 text-blue-600 border-0 text-[10px]">{u.school_name || u.school_id}</Badge>
                      : <span className="text-xs text-gray-400">Platform level</span>}
                  </td>
                  <td className="py-3 px-4">
                    <Badge className={`border-0 text-[10px] ${STATUS_STYLE[u.account_status] || 'bg-gray-100 text-gray-500'}`} data-testid={`user-status-${u.id}`}>
                      {u.account_status_label}
                    </Badge>
                  </td>
                  <td className="py-3 px-4 text-gray-500 whitespace-nowrap">{u.last_login_at ? fmt(u.last_login_at) : 'Never'}</td>
                  <td className="py-3 px-4 text-gray-500 whitespace-nowrap">{fmt(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {loading && <p className="text-center text-sm text-gray-400 py-8">Loading accounts…</p>}
        {!loading && users.length === 0 && <p className="text-center text-sm text-gray-400 py-8" data-testid="users-empty">No accounts match these filters</p>}
      </div>

      <p className="text-[11px] text-gray-400 mt-3">
        Students are school-managed profiles, not login accounts, so they never appear here.
      </p>

      {createOpen && (
        <CreateUserDialog schools={schools} onClose={() => setCreateOpen(false)}
                          onDone={() => { setCreateOpen(false); load(); }} />
      )}
      {detailId && (
        <UserDetailDialog userId={detailId} onClose={() => setDetailId(null)} onChanged={load} />
      )}
    </div>
  );
}
